import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  Save,
  Plus,
  X,
  Trash2
} from "lucide-react";

const INDUSTRIES = [
  "Technology", "Healthcare", "Finance", "Education", "Marketing",
  "Sales", "Engineering", "Design", "Legal", "Manufacturing"
];

const SENIORITY_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior Level" },
  { value: "lead", label: "Lead / Manager" },
  { value: "executive", label: "Executive" }
];

const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" }
];

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "SGD", "AED", "CHF", "JPY"];

const EditJob = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [jobData, setJobData] = useState({
    title: "",
    industry: "",
    seniority_level: "mid",
    employment_type: "full-time",
    skills: [],
    location: "",
    description: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "USD",
    status: "active"
  });
  const [newSkill, setNewSkill] = useState("");

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setJobData({
          title: data.title || "",
          industry: data.industry || "",
          seniority_level: data.seniority_level || "mid",
          employment_type: data.employment_type || "full-time",
          skills: data.skills || [],
          location: data.location || "",
          description: data.description || "",
          salary_min: data.salary_min || "",
          salary_max: data.salary_max || "",
          salary_currency: data.salary_currency || "USD",
          status: data.status || "active"
        });
      } else {
        toast.error("Job not found");
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to fetch job:", error);
      toast.error("Failed to load job");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!jobData.title || !jobData.industry) {
      toast.error("Title and industry are required");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...jobData,
          salary_min: jobData.salary_min ? parseFloat(jobData.salary_min) : null,
          salary_max: jobData.salary_max ? parseFloat(jobData.salary_max) : null,
        }),
      });

      if (response.ok) {
        toast.success("Job updated successfully!");
        navigate(`/jobs/${jobId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update job");
      }
    } catch (error) {
      toast.error("Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this job posting? This action cannot be undone.")) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Job deleted successfully");
        navigate("/dashboard/recruiter");
      } else {
        toast.error("Failed to delete job");
      }
    } catch (error) {
      toast.error("Failed to delete job");
    } finally {
      setDeleting(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !jobData.skills.includes(newSkill.trim())) {
      setJobData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill("");
    }
  };

  const removeSkill = (skill) => {
    setJobData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                EDIT <span className="text-crimson">JOB POSTING</span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleDelete}
                disabled={deleting}
                variant="outline"
                className="border-red-500/50 text-red-500 hover:bg-red-500 hover:text-white"
                data-testid="delete-job-btn"
              >
                {deleting ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider"
                data-testid="save-job-btn"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <motion.div 
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h3 className="font-heading text-lg font-bold mb-4">JOB DETAILS</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Job Title *
                  </Label>
                  <Input
                    value={jobData.title}
                    onChange={(e) => setJobData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Senior Frontend Developer"
                    className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                    data-testid="job-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Industry *
                    </Label>
                    <select
                      value={jobData.industry}
                      onChange={(e) => setJobData(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="">Select Industry</option>
                      {INDUSTRIES.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Status
                    </Label>
                    <select
                      value={jobData.status}
                      onChange={(e) => setJobData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Seniority Level
                    </Label>
                    <select
                      value={jobData.seniority_level}
                      onChange={(e) => setJobData(prev => ({ ...prev, seniority_level: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      {SENIORITY_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Employment Type
                    </Label>
                    <select
                      value={jobData.employment_type}
                      onChange={(e) => setJobData(prev => ({ ...prev, employment_type: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      {EMPLOYMENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Location
                  </Label>
                  <Input
                    value={jobData.location}
                    onChange={(e) => setJobData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., New York, NY or Remote"
                    className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Currency
                    </Label>
                    <select
                      value={jobData.salary_currency}
                      onChange={(e) => setJobData(prev => ({ ...prev, salary_currency: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      {CURRENCIES.map(curr => (
                        <option key={curr} value={curr}>{curr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Min Salary
                    </Label>
                    <Input
                      type="number"
                      value={jobData.salary_min}
                      onChange={(e) => setJobData(prev => ({ ...prev, salary_min: e.target.value }))}
                      placeholder="e.g., 80000"
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Max Salary
                    </Label>
                    <Input
                      type="number"
                      value={jobData.salary_max}
                      onChange={(e) => setJobData(prev => ({ ...prev, salary_max: e.target.value }))}
                      placeholder="e.g., 120000"
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Skills */}
            <motion.div 
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="font-heading text-lg font-bold mb-4">KEY SKILLS</h3>
              
              <div className="flex gap-2 mb-4">
                <Input
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addSkill()}
                  placeholder="Add required skill"
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                />
                <Button onClick={addSkill} className="bg-crimson hover:bg-crimson-hover text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {jobData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson"
                  >
                    {skill}
                    <button onClick={() => removeSkill(skill)} className="hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div 
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-heading text-lg font-bold mb-4">JOB DESCRIPTION</h3>
              <Textarea
                value={jobData.description}
                onChange={(e) => setJobData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed job description..."
                className="bg-obsidian border-[#333] text-white min-h-[300px] font-mono text-sm"
                data-testid="description-textarea"
              />
            </motion.div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6"
                data-testid="save-job-bottom-btn"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditJob;
